import * as React from "react";
import { toast } from "sonner";

import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/lib/shadcn/ui/alert-dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { useReactivateApartmentMutation } from "@/store/slices/apartments";

/** The minimal apartment shape this dialog needs. */
export interface ReactivateDialogApartment {
    id: number;
    name: string;
}

export interface ReactivateApartmentDialogProps {
    apartment: ReactivateDialogApartment;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Confirm-only mistake-recovery dialog (FR-011, apartment-decommissioning).
 * Reactivating clears the removal date and treats the span as continuously in
 * operation (no gap modelled); trimmed/removed blocks are NOT restored (FR-012).
 */
export const ReactivateApartmentDialog: React.FC<ReactivateApartmentDialogProps> = ({
    apartment,
    open,
    onOpenChange,
}) => {
    const [reactivate, { isLoading }] = useReactivateApartmentMutation();

    const handleConfirm = async () => {
        // A plain Button (not AlertDialogAction) so Radix never auto-closes; we keep
        // the dialog open on error and close it ourselves on success.
        if (isLoading) {
            return;
        }
        try {
            await reactivate(apartment.id).unwrap();
            toast.success(`${apartment.name} reactivated.`);
            onOpenChange(false);
        } catch {
            toast.error(`Failed to reactivate ${apartment.name}. Please try again.`);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent
                data-testid="reactivate-apartment-dialog"
                onEscapeKeyDown={(event) => {
                    if (isLoading) {
                        event.preventDefault();
                    }
                }}
            >
                <AlertDialogHeader>
                    <AlertDialogTitle>Reactivate {apartment.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This clears the removal date and returns the apartment to active lists and counts. It is
                        treated as continuously in operation — no inactive gap is recorded. Blocks that were trimmed
                        or removed when it was decommissioned are not restored.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
                    <Button type="button" data-testid="reactivate-confirm" onClick={handleConfirm} disabled={isLoading}>
                        {isLoading ? "Reactivating…" : "Reactivate"}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default ReactivateApartmentDialog;
